/**
 * @file useBinSelection.ts
 * @description Отметки ячеек под печать этикеток (phase19.22 шаг 5):
 *   галочки на ячейках, «весь стеллаж», «все видимые», снять выбор.
 *
 * Отмечаются только активные ячейки: архивной адрес на складе не нужен.
 * Выбранные считаются от текущего списка (один источник правды) — ячейка,
 * ушедшая в архив после обновления, из выбора выпадает сама. Смена склада
 * сбрасывает выбор: ячейки другого склада не печатаются вместе.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import { groupRacks, selectionScope } from "./bin-label-print";
import type { StorageBinDto } from "../../../lib/api-bins";

export function useBinSelection(bins: StorageBinDto[], warehouseId: string) {
  const [ids, setIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    setIds(new Set());
  }, [warehouseId]);

  const selected = useMemo(() => bins.filter((b) => b.isActive && ids.has(b.id)), [bins, ids]);
  const racks = useMemo(() => groupRacks(bins), [bins]);
  const scope = useMemo(() => selectionScope(selected, bins), [selected, bins]);

  /** Отметить или снять набор ячеек разом. */
  const setMany = useCallback((binIds: string[], value: boolean) => {
    setIds((prev) => {
      const next = new Set(prev);
      for (const id of binIds) {
        if (value) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }, []);

  const toggle = useCallback(
    (binId: string) => setMany([binId], !ids.has(binId)),
    [ids, setMany],
  );

  /** Все ли активные ячейки стеллажа отмечены. */
  const isRackSelected = useCallback(
    (rack: string) => {
      const group = racks.find((r) => r.rack === rack);
      return !!group && group.binIds.length > 0 && group.binIds.every((id) => ids.has(id));
    },
    [racks, ids],
  );

  /** «Стеллаж A»: отметить все его ячейки или снять, если уже отмечены. */
  const toggleRack = useCallback(
    (rack: string) => {
      const group = racks.find((r) => r.rack === rack);
      if (group) setMany(group.binIds, !isRackSelected(rack));
    },
    [racks, isRackSelected, setMany],
  );

  return {
    selected,
    count: selected.length,
    racks,
    scope,
    isSelected: (binId: string) => ids.has(binId),
    toggle,
    setMany,
    isRackSelected,
    toggleRack,
    clear: () => setIds(new Set()),
  };
}

export type BinSelection = ReturnType<typeof useBinSelection>;
